import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, Plus, Eye, Image as ImageIcon, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import AlertUploadDialog from './AlertUploadDialog';
import AlertViewerModal from './AlertViewerModal';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { alertsAPI } from '@/lib/alerts'; // ✅ إضافة API import

const SystemAlertsWidget = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === 'Admin';

  const [alerts, setAlerts] = useState([]);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ✅ جلب البيانات من API
  const loadData = async () => {
    try {
      setIsLoading(true);
      const response = await alertsAPI.list();
      
      if (response.success) {
        setAlerts(response.alerts || []);
      } else {
        console.error('Failed to load alerts:', response.error);
        setAlerts([]);
      }
    } catch (error) {
      console.error('Load alerts error:', error);
      toast({
        title: "⚠️ Alerts Load Failed",
        description: "Could not load system alerts. Please refresh.",
        variant: "destructive"
      });
      setAlerts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  // ✅ حذف عبر API
  const handleDeleteAlert = async (e, id) => {
    e.stopPropagation();
    if (!isAdmin) return;

    if (window.confirm("Are you sure you want to delete this alert?")) {
      try {
        const response = await alertsAPI.dismiss(id);
        
        if (response.success) {
          loadData(); // إعادة تحميل
          toast({
            title: "✅ Alert Deleted",
            description: "The alert has been removed successfully.",
            className: "bg-green-50 text-green-800 border-green-200"
          });
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        console.error('Delete error:', error);
        toast({
          title: "❌ Delete Failed",
          description: error.message || "Could not delete the alert.",
          variant: "destructive"
        });
      }
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'critical': "bg-red-100 text-red-700 border-red-200",
      'high': "bg-red-100 text-red-700 border-red-200",
      'medium': "bg-amber-100 text-amber-700 border-amber-200",
      'low': "bg-emerald-100 text-emerald-700 border-emerald-200"
    };
    
    return (
      <Badge className={cn("px-2 py-0.5 text-xs font-medium", colors[priority] || colors.medium)}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-full border-stone-200/60 shadow-md">
        <CardHeader className="border-b border-stone-100 bg-slate-50/50 pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-xl">
              <AlertCircle className="w-6 h-6 text-red-500" />
              System Alerts ({alerts.length})
            </CardTitle>
            {isAdmin && (
              <Button 
                size="sm" 
                onClick={() => setIsUploadOpen(true)} 
                className="h-8 text-xs gap-1"
              >
                <Plus className="w-4 h-4" />
                Post Alert
              </Button>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="pt-6 max-h-[500px] overflow-y-auto">
          {/* Image Alerts Section */}
          {alerts.length > 0 ? (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="group relative bg-gradient-to-r from-slate-50 to-slate-100 p-4 rounded-xl hover:shadow-md transition-all border hover:border-blue-200 cursor-pointer"
                  onClick={() => setSelectedAlert(alert)}
                >
                  {/* Thumbnail */}
                  {alert.file_name && (
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-200 absolute -top-2 -right-2 shadow-lg group-hover:scale-105 transition-transform">
                      <img
                        src={`https://new.sbdemolition.online/uploads/alerts/${alert.file_name}`}
                        alt={alert.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="w-full h-full flex items-center justify-center text-xs text-slate-500 hidden">
                        <ImageIcon className="w-6 h-6" />
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="pr-24">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-slate-900 text-base truncate max-w-[250px]">
                        {alert.title}
                      </h4>
                      {getPriorityBadge(alert.priority)}
                    </div>
                    <p className="text-xs text-slate-600 mb-1 line-clamp-2">
                      {alert.message}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>{alert.created_by_name || 'System'}</span>
                    </div>
                  </div>

                  {/* Delete Button (Admin Only, Hover) */}
                  {isAdmin && (
                    <button
                      onClick={(e) => handleDeleteAlert(e, alert.id)}
                      className="absolute top-2 right-2 p-1.5 text-stone-300 hover:text-red-500 hover:bg-red-50 rounded-md opacity-0 group-hover:opacity-100 transition-all shadow-sm"
                      title="Delete Alert"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm font-medium">No active image alerts</p>
              {isAdmin && (
                <p className="text-xs mt-1">Be the first to post an important announcement!</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertUploadDialog
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUploadSuccess={loadData}
      />
      
      <AlertViewerModal
        isOpen={!!selectedAlert}
        onClose={() => setSelectedAlert(null)}
        alert={selectedAlert}
      />
    </>
  );
};

export default SystemAlertsWidget;
